import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import adminFacilitatorService from '../../services/adminFacilitatorService';
import { toast, ToastContainer } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUserFriends, 
  faPlus,
  faSearch,
  faEdit,
  faTrash,
  faCheckCircle,
  faTimesCircle,
  faChartBar,
  faUsers,
  faInbox
} from '@fortawesome/free-solid-svg-icons';
import 'react-toastify/dist/ReactToastify.css';
import CreateFacilitatorModal from '../../components/admin/CreateFacilitatorModal';
import EditFacilitatorModal from '../../components/admin/EditFacilitatorModal';

const SPECIALIZATIONS = [
  'addiction',
  'work_stress',
  'academic_stress',
  'grief',
  'heartbreak',
  'anxiety',
  'depression',
  'burnout',
  'relationship_issues',
  'life_transitions'
];

const AdminFacilitatorsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [facilitators, setFacilitators] = useState([]);
  const [stats, setStats] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingFacilitator, setEditingFacilitator] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [facilitatorsResponse, statsResponse] = await Promise.all([
        adminFacilitatorService.getAllFacilitators(),
        adminFacilitatorService.getStats()
      ]);

      setFacilitators(facilitatorsResponse.facilitators || []);
      setStats(statsResponse);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load facilitators');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFacilitator = async (facilitatorId) => {
    if (!window.confirm('Are you sure you want to remove this facilitator designation? This cannot be undone if they have active matches.')) {
      return;
    }

    try {
      await adminFacilitatorService.deleteFacilitator(facilitatorId);
      toast.success('Facilitator removed successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete facilitator');
    }
  };

  const filteredFacilitators = facilitators.filter(f => 
    f.user?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.user?.username?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
            <FontAwesomeIcon icon={faUserFriends} style={{ color: '#FFB6C1' }} spin />
          </div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <ToastContainer position="top-right" autoClose={3000} theme="dark" />
      
      <div className="admin-header">
        <h1>
          <FontAwesomeIcon icon={faUserFriends} /> Facilitators
        </h1>
        <p>Designate and manage buddy facilitators</p>
      </div>
      
      <div>

        {/* Stats Cards */}
        {stats && (
          <div className="admin-stats-grid">
            <div className="admin-stat-card">
              <div className="admin-stat-header">
                <div className="admin-stat-icon success">
                  <FontAwesomeIcon icon={faUserFriends} />
                </div>
              </div>
              <div className="admin-stat-value">{stats.facilitators?.active || 0}</div>
              <div className="admin-stat-label">Active Facilitators</div>
            </div>

            <div className="admin-stat-card">
              <div className="admin-stat-header">
                <div className="admin-stat-icon warning">
                  <FontAwesomeIcon icon={faInbox} />
                </div>
              </div>
              <div className="admin-stat-value">{stats.requests?.pending || 0}</div>
              <div className="admin-stat-label">Pending Requests</div>
            </div>

            <div className="admin-stat-card">
              <div className="admin-stat-header">
                <div className="admin-stat-icon primary">
                  <FontAwesomeIcon icon={faUsers} />
                </div>
              </div>
              <div className="admin-stat-value">{stats.matches?.active || 0}</div>
              <div className="admin-stat-label">Active Matches</div>
            </div>

            <div className="admin-stat-card">
              <div className="admin-stat-header">
                <div className="admin-stat-icon primary">
                  <FontAwesomeIcon icon={faChartBar} />
                </div>
              </div>
              <div className="admin-stat-value">{stats.requests?.total || 0}</div>
              <div className="admin-stat-label">Total Requests</div>
            </div>
          </div>
        )}

        {/* Search and Create */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          {/* Search */}
          <div style={{ flex: 1, position: 'relative' }}>
            <FontAwesomeIcon 
              icon={faSearch} 
              style={{
                position: 'absolute',
                left: '1rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#999'
              }}
            />
            <input
              type="text"
              placeholder="Search facilitators by name, email, or username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem 1rem 0.75rem 2.5rem',
                backgroundColor: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem'
              }}
            />
          </div>

          {/* Create Button */}
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#FFB6C1',
              border: 'none',
              borderRadius: '8px',
              color: '#000',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            <FontAwesomeIcon icon={faPlus} /> Create Facilitator
          </button>
        </div>

        {/* Facilitators Table */}
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '12px',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch'
        }}>
          {filteredFacilitators.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center' }}>
              <FontAwesomeIcon icon={faUserFriends} size="3x" style={{ color: '#666', marginBottom: '1rem' }} />
              <p style={{ color: '#999' }}>
                {searchTerm ? 'No facilitators found matching your search' : 'No facilitators yet'}
              </p>
              {!searchTerm && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  style={{
                    marginTop: '1rem',
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#FFB6C1',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#000',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} /> Create First Facilitator
                </button>
              )}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#000', borderBottom: '1px solid #333' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', color: '#FFB6C1', fontWeight: '600' }}>
                    Facilitator
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'left', color: '#FFB6C1', fontWeight: '600' }}>
                    Specializations
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'center', color: '#FFB6C1', fontWeight: '600' }}>
                    Capacity
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'center', color: '#FFB6C1', fontWeight: '600' }}>
                    Status
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'right', color: '#FFB6C1', fontWeight: '600' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredFacilitators.map((facilitator, index) => (
                  <tr 
                    key={facilitator.id}
                    style={{ 
                      borderBottom: index < filteredFacilitators.length - 1 ? '1px solid #333' : 'none'
                    }}
                  >
                    {/* Facilitator Info */}
                    <td data-label="Facilitator" style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '50%',
                          backgroundColor: '#FFB6C1',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.5rem',
                          fontWeight: '700',
                          color: '#000'
                        }}>
                          {facilitator.user?.full_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <div style={{ color: '#FFB6C1', fontWeight: '600', marginBottom: '0.25rem' }}>
                            {facilitator.user?.full_name || 'Unknown'}
                          </div>
                          <div style={{ color: '#999', fontSize: '0.85rem' }}>
                            {facilitator.user?.email}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Specializations */}
                    <td data-label="Specializations" style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', maxWidth: '300px' }}>
                        {facilitator.specializations && facilitator.specializations.length > 0 ? (
                          facilitator.specializations.slice(0, 3).map((spec, i) => (
                            <span
                              key={i}
                              style={{
                                padding: '0.25rem 0.625rem',
                                backgroundColor: '#000',
                                border: '1px solid #FFB6C1',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                color: '#FFB6C1'
                              }}
                            >
                              {spec}
                            </span>
                          ))
                        ) : (
                          <span style={{ color: '#666', fontSize: '0.85rem' }}>None</span>
                        )}
                        {facilitator.specializations && facilitator.specializations.length > 3 && (
                          <span style={{ color: '#999', fontSize: '0.75rem' }}>
                            +{facilitator.specializations.length - 3} more
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Capacity */}
                    <td data-label="Capacity" style={{ padding: '1rem', textAlign: 'center' }}>
                      <div style={{ color: '#ccc', fontWeight: '600' }}>
                        {facilitator.active_matches || 0} / {facilitator.max_active_matches || 10}
                      </div>
                      <div style={{
                        width: '100px',
                        height: '6px',
                        backgroundColor: '#000',
                        borderRadius: '3px',
                        margin: '0.5rem auto 0',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${((facilitator.active_matches || 0) / (facilitator.max_active_matches || 10)) * 100}%`,
                          height: '100%',
                          backgroundColor: (facilitator.active_matches || 0) >= (facilitator.max_active_matches || 10) 
                            ? '#ef4444' 
                            : '#10b981',
                          transition: 'width 0.3s'
                        }} />
                      </div>
                    </td>

                    {/* Status */}
                    <td data-label="Status" style={{ padding: '1rem', textAlign: 'center' }}>
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.375rem 0.75rem',
                        backgroundColor: facilitator.is_active ? '#10b98120' : '#ef444420',
                        border: `1px solid ${facilitator.is_active ? '#10b981' : '#ef4444'}`,
                        borderRadius: '20px'
                      }}>
                        <FontAwesomeIcon 
                          icon={facilitator.is_active ? faCheckCircle : faTimesCircle} 
                          style={{ color: facilitator.is_active ? '#10b981' : '#ef4444' }}
                        />
                        <span style={{ 
                          color: facilitator.is_active ? '#10b981' : '#ef4444',
                          fontSize: '0.85rem',
                          fontWeight: '600'
                        }}>
                          {facilitator.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </td>

                    {/* Actions */}
                    <td data-label="Actions" style={{ padding: '1rem', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => setEditingFacilitator(facilitator)}
                          style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: '#3b82f6',
                            border: 'none',
                            borderRadius: '6px',
                            color: '#fff',
                            cursor: 'pointer',
                            fontSize: '0.9rem'
                          }}
                        >
                          <FontAwesomeIcon icon={faEdit} /> Edit
                        </button>
                        <button
                          onClick={() => handleDeleteFacilitator(facilitator.id)}
                          style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: '#ef4444',
                            border: 'none',
                            borderRadius: '6px',
                            color: '#fff',
                            cursor: 'pointer',
                            fontSize: '0.9rem'
                          }}
                        >
                          <FontAwesomeIcon icon={faTrash} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateFacilitatorModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadData();
          }}
          specializations={SPECIALIZATIONS}
        />
      )}

      {/* Edit Modal */}
      {editingFacilitator && (
        <EditFacilitatorModal
          facilitator={editingFacilitator}
          onClose={() => setEditingFacilitator(null)}
          onSuccess={() => {
            setEditingFacilitator(null);
            loadData();
          }}
          specializations={SPECIALIZATIONS}
        />
      )}
    </div>
  );
};

export default AdminFacilitatorsPage;
